// ========================================================
// FAST HIT Website — SEO & Meta Tags Hook (Phase 9)
// Dynamically sets document title, meta tags, and structured JSON-LD
// ========================================================

import { useEffect } from 'react';

export interface PageMetadataOptions {
  title: string;
  description: string;
  canonicalPath?: string;
  ogType?: 'website' | 'article' | 'profile';
  ogImage?: string;
  jsonLd?: Record<string, unknown>;
  noIndex?: boolean;
}

export function usePageMetadata({
  title,
  description,
  canonicalPath = '',
  ogType = 'website',
  ogImage = 'https://fasthit.in/logo.png',
  jsonLd,
  noIndex = false,
}: PageMetadataOptions) {
  useEffect(() => {
    // 1. Set Document Title
    const fullTitle = title.includes('FAST HIT') ? title : `${title} | FAST HIT Cricket`;
    document.title = fullTitle;

    // Helper to set or create meta tag
    const setMetaTag = (attrName: string, attrVal: string, content: string) => {
      let el = document.querySelector(`meta[${attrName}="${attrVal}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    // 2. Standard Meta Tags
    setMetaTag('name', 'description', description);
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:image', ogImage);

    // 3. Robots (noindex if flagged)
    if (noIndex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      setMetaTag('name', 'robots', 'index, follow');
    }

    // 4. Canonical Link
    const canonicalUrl = `https://fasthit.in${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;
    let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!linkEl) {
      linkEl = document.createElement('link');
      linkEl.rel = 'canonical';
      document.head.appendChild(linkEl);
    }
    linkEl.href = canonicalUrl;

    // 5. JSON-LD Structured Data
    let scriptEl: HTMLScriptElement | null = null;
    if (jsonLd) {
      scriptEl = document.createElement('script');
      scriptEl.type = 'application/ld+json';
      scriptEl.text = JSON.stringify(jsonLd);
      document.head.appendChild(scriptEl);
    }

    return () => {
      if (scriptEl && scriptEl.parentNode) {
        scriptEl.parentNode.removeChild(scriptEl);
      }
    };
  }, [title, description, canonicalPath, ogType, ogImage, jsonLd, noIndex]);
}
